import * as React from "react";
import { useState, useEffect } from "react";
import { Loader } from "lucide-react";
import { Button } from "@/taskpane/components/ui/button";
import OnboardingHeader from "../OnboardingHeader";

interface EmailSentProps {
  onBackToLogin: () => void;
  onResendLink: () => void;
  isResending: boolean;
  email: string;
}

const RESEND_COOLDOWN_SECONDS = 30;

const EmailSent: React.FC<EmailSentProps> = ({ onBackToLogin, onResendLink, isResending, email }) => {
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);

  const isResendDisabled = countdown > 0 || isResending;

  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const handleResend = () => {
    if (isResendDisabled) return;
    onResendLink();
    setCountdown(RESEND_COOLDOWN_SECONDS);
  };

  return (
    <div className="flex flex-col gap-4 w-full max-w-md mx-auto">
      <OnboardingHeader onBack={onBackToLogin} />

      <section
        className="flex flex-col items-center gap-5 w-full text-center"
        role="main"
        aria-labelledby="email-sent-heading"
        aria-describedby="email-sent-description"
      >
        <h2 id="email-sent-heading" className="text-xl font-medium m-0">
          Check Your Email
        </h2>

        <p id="email-sent-description" className="text-sm text-neutral-400 m-0 leading-normal">
          We've sent a password reset link to:
        </p>

        <p className="text-sm font-semibold text-neutral-200 m-0">{email}</p>

        <p className="text-xs text-neutral-400 leading-snug m-0">
          Click the link in the email to reset your password. The link will open in your web browser where you can create
          a new password. After resetting, you can return here and log in with your new password.
        </p>

        <p className="text-xs text-neutral-400 leading-snug m-0">
          Didn't receive the email? Check your spam folder or try again.
        </p>

        <div className="flex flex-col w-full gap-2 mt-2">
          <Button
            size="lg"
            variant="outline"
            className="w-full"
            onClick={handleResend}
            disabled={isResendDisabled}
          >
            {isResending ? (
              <>
                <Loader className="animate-spin mr-2" />
                Sending...
              </>
            ) : countdown > 0 ? (
              `Resend Link (${countdown}s)`
            ) : (
              "Resend Link"
            )}
          </Button>

          <Button size="lg" className="w-full" onClick={onBackToLogin}>
            Back to Login
          </Button>
        </div>
      </section>
    </div>
  );
};

export default EmailSent;